import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../../services/auth.service";
import "../../styles/auth.css";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromBank =
    location.pathname.startsWith("/bank/forgot-password") ||
    String(searchParams.get("from") || "").toLowerCase() === "bank";
  const loginPath = fromBank ? "/bank/login" : "/";
  const portalPayload = fromBank ? { portal: "bank" } : {};

  const [step, setStep] = useState(1);

  const [mobile, setMobile] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");

  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const clearMessages = () => {
    setMessage("");
    setError("");
  };

  /*
   * STEP 1
   *
   * Find all active accounts registered
   * with this mobile number.
   */
  const handleFindAccounts = async (e) => {
    e.preventDefault();

    clearMessages();

    const phone = mobile.trim();

    if (!phone) {
      setError("Please enter your registered mobile number.");
      return;
    }

    setLoading(true);

    try {
      const response =
        await authService.findForgotPasswordAccounts({
          phone,
          ...portalPayload,
        });

      const foundAccounts =
        response?.data?.accounts ||
        response?.accounts ||
        [];

      /*
       * No account found.
       */
      if (foundAccounts.length === 0) {
        setError(
          "No active account was found for this mobile number."
        );
        return;
      }

      /*
       * Only one account:
       * directly send OTP.
       */
      if (foundAccounts.length === 1) {
        const account = foundAccounts[0];

        setAccounts(foundAccounts);
        setSelectedUserId(String(account.id));

        await sendOtpForAccount(
          String(account.id),
          phone,
          account
        );

        return;
      }

      /*
       * Multiple accounts:
       * show account selection screen.
       */
      setAccounts(foundAccounts);
      setSelectedUserId("");

      setMessage(
        "Multiple accounts were found. Please select the account you want to recover."
      );

      setStep(2);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to find your account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Send OTP for the exact selected account.
   */
  const sendOtpForAccount = async (
    userId,
    phone,
    account
  ) => {
    clearMessages();

    setLoading(true);

    try {
      const response =
        await authService.sendForgotPasswordOtp({
          phone,
          userId,
          ...portalPayload,
        });

      setSelectedUserId(String(userId));

      setMessage(
        response?.message ||
          `OTP sent successfully for ${account?.roleLabel || "your account"}.`
      );

      setStep(3);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to send OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * STEP 2
   *
   * User selects one account when
   * multiple accounts share the same mobile.
   */
  const handleSelectAccount = async (account) => {
    if (!account?.id) {
      setError("Invalid account selection.");
      return;
    }

    const phone = mobile.trim();

    setSelectedUserId(String(account.id));

    await sendOtpForAccount(
      String(account.id),
      phone,
      account
    );
  };

  /*
   * STEP 3
   *
   * Verify OTP for the selected account.
   */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    clearMessages();

    const phone = mobile.trim();
    const otpValue = otp.trim();

    if (!selectedUserId) {
      setError(
        "Please select an account before verifying OTP."
      );
      return;
    }

    if (!otpValue) {
      setError("Please enter the OTP.");
      return;
    }

    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }

    setLoading(true);

    try {
      const response =
        await authService.verifyForgotPasswordOtp({
          phone,
          userId: selectedUserId,
          otp: otpValue,
        });

      const token =
        response?.resetToken ||
        response?.data?.resetToken ||
        response?.data?.reset_token;

      if (!token) {
        setError(
          "OTP verified, but reset token was not received."
        );
        return;
      }

      setResetToken(token);

      setMessage(
        response?.message ||
          "OTP verified successfully."
      );

      setStep(4);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Invalid or expired OTP."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * STEP 4
   *
   * Set the new password.
   */
  const handleResetPassword = async (e) => {
    e.preventDefault();

    clearMessages();

    if (!newPassword) {
      setError("Please enter your new password.");
      return;
    }

    if (newPassword.length < 8) {
      setError(
        "Password must be at least 8 characters."
      );
      return;
    }

    if (!confirmPassword) {
      setError(
        "Please confirm your new password."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(
        "New password and confirm password do not match."
      );
      return;
    }

    if (!resetToken) {
      setError(
        "Reset session has expired. Please request a new OTP."
      );

      setStep(1);
      setOtp("");
      setResetToken("");
      return;
    }

    setLoading(true);

    try {
      const response =
        await authService.resetPasswordWithOtp({
          resetToken,
          newPassword,
        });

      setMessage(
        response?.message ||
          "Password reset successfully. You can now login."
      );

      setTimeout(() => {
        navigate(loginPath);
      }, 1500);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Change mobile number.
   */
  const handleChangeMobile = () => {
    clearMessages();

    setMobile("");
    setAccounts([]);
    setSelectedUserId("");
    setOtp("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");

    setStep(1);
  };

  /*
   * Go back from account selection.
   */
  const handleBackToMobile = () => {
    clearMessages();

    setAccounts([]);
    setSelectedUserId("");

    setStep(1);
  };

  /*
   * Resend/change account.
   *
   * We keep the selected account so that
   * OTP remains tied to the exact user.
   */
  const handleChangeAccount = () => {
    clearMessages();

    setOtp("");
    setSelectedUserId("");

    setStep(2);
  };

  const selectedAccount = accounts.find(
    (account) =>
      String(account.id) ===
      String(selectedUserId)
  );

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2>{fromBank ? "Bank / NBFC Forgot Password" : "Forgot Password"}</h2>

        {message && (
          <p
            className="auth-info"
            role="status"
          >
            {message}
          </p>
        )}

        {error && (
          <p
            className="auth-error"
            role="alert"
          >
            {error}
          </p>
        )}

        {/* =========================
            STEP 1 - MOBILE
        ========================== */}
        {step === 1 && (
          <form onSubmit={handleFindAccounts}>
            <p className="auth-info">
              {fromBank
                ? "Enter the registered mobile number of your Bank / NBFC Admin or Bank / NBFC User account."
                : "Enter your registered mobile number to recover your account."}
            </p>

            <input
              type="tel"
              value={mobile}
              onChange={(e) =>
                setMobile(e.target.value)
              }
              placeholder="Registered mobile number"
              autoComplete="tel"
              disabled={loading}
            />

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading
                ? "Checking Account..."
                : "Continue"}
            </button>
          </form>
        )}

        {/* =========================
            STEP 2 - ACCOUNT SELECTION
        ========================== */}
        {step === 2 && (
          <div>
            <p className="auth-info">
              Multiple accounts are registered
              with this mobile number.
              <br />
              Please select the account for which
              you want to reset the password.
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginTop: "16px",
                marginBottom: "16px",
              }}
            >
              {accounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  className="auth-button"
                  onClick={() =>
                    handleSelectAccount(account)
                  }
                  disabled={loading}
                  style={{
                    textAlign: "left",
                    padding: "14px 16px",
                  }}
                >
                  <strong>
                    {account.roleLabel ||
                      account.role ||
                      "User"}
                  </strong>

                  {account.name && (
                    <div
                      style={{
                        marginTop: "4px",
                      }}
                    >
                      {account.name}
                    </div>
                  )}

                  {account.agencyName && (
                    <div
                      style={{
                        marginTop: "3px",
                        fontSize: "13px",
                      }}
                    >
                      {account.agencyName}
                    </div>
                  )}

                  {account.branchName && (
                    <div
                      style={{
                        marginTop: "3px",
                        fontSize: "13px",
                      }}
                    >
                      Branch:{" "}
                      {account.branchName}
                    </div>
                  )}

                  {account.employeeNumber && (
                    <div
                      style={{
                        marginTop: "3px",
                        fontSize: "13px",
                      }}
                    >
                      Employee No:{" "}
                      {account.employeeNumber}
                    </div>
                  )}

                  {account.maskedEmail && (
                    <div
                      style={{
                        marginTop: "3px",
                        fontSize: "13px",
                        opacity: 0.8,
                      }}
                    >
                      {account.maskedEmail}
                    </div>
                  )}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="auth-button"
              onClick={handleBackToMobile}
              disabled={loading}
            >
              Change Mobile Number
            </button>
          </div>
        )}

        {/* =========================
            STEP 3 - OTP
        ========================== */}
        {step === 3 && (
          <form onSubmit={handleVerifyOtp}>
            {selectedAccount && (
              <div
                className="auth-info"
                style={{
                  marginBottom: "12px",
                }}
              >
                <strong>
                  {selectedAccount.roleLabel ||
                    selectedAccount.role}
                </strong>

                {selectedAccount.name && (
                  <div>
                    {selectedAccount.name}
                  </div>
                )}

                {selectedAccount.agencyName && (
                  <div>
                    {selectedAccount.agencyName}
                  </div>
                )}
              </div>
            )}

            <p className="auth-info">
              Enter the 6-digit OTP sent to your
              registered mobile number.
            </p>

            <input
              type="text"
              value={otp}
              onChange={(e) =>
                setOtp(
                  e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 6)
                )
              }
              placeholder="Enter 6-digit OTP"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              disabled={loading}
            />

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading
                ? "Verifying..."
                : "Verify OTP"}
            </button>

            <button
              type="button"
              className="auth-button"
              onClick={handleChangeAccount}
              disabled={loading}
            >
              Change Account
            </button>

            <button
              type="button"
              className="auth-button"
              onClick={handleChangeMobile}
              disabled={loading}
            >
              Change Mobile Number
            </button>
          </form>
        )}

        {/* =========================
            STEP 4 - NEW PASSWORD
        ========================== */}
        {step === 4 && (
          <form onSubmit={handleResetPassword}>
            {selectedAccount && (
              <div
                className="auth-info"
                style={{
                  marginBottom: "12px",
                }}
              >
                Resetting password for:

                <strong
                  style={{
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {selectedAccount.name}
                </strong>

                <span
                  style={{
                    display: "block",
                    marginTop: "2px",
                  }}
                >
                  {selectedAccount.roleLabel ||
                    selectedAccount.role}
                </span>
              </div>
            )}

            <p className="auth-info">
              Enter your new password below.
            </p>

            <input
              type="password"
              value={newPassword}
              onChange={(e) =>
                setNewPassword(e.target.value)
              }
              placeholder="New password"
              autoComplete="new-password"
              disabled={loading}
            />

            <input
              type="password"
              value={confirmPassword}
              onChange={(e) =>
                setConfirmPassword(
                  e.target.value
                )
              }
              placeholder="Confirm new password"
              autoComplete="new-password"
              disabled={loading}
            />

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading
                ? "Resetting Password..."
                : "Reset Password"}
            </button>
          </form>
        )}

        <button
          type="button"
          className="auth-button"
          onClick={() => navigate(loginPath)}
          disabled={loading}
        >
          {fromBank ? "Back to Bank / NBFC Login" : "Back to Login Portal"}
        </button>
      </div>
    </div>
  );
}